import { FileDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_PAGE_SIZE, safeFormatDate } from "../../../../utils/formatters";
import { DataTable, type Column } from "../../../components/DataTable";
import { SearchInput } from "../../../components/SearchInput";
import { Button } from "../../../components/ui/button";
import { completedCount } from "../data/pagPreviousCycle";
import {
  PAG_BREAKDOWN_SEGMENTS,
  type PagCycle,
  type PagPolicyBreakdown,
  type PagReviewRow,
} from "../data/pagTypes";
import {
  fetchPagCurrentCycleList,
  fetchPagPreviousCycleList,
  type PagCycleListResponse,
  type PagDrilldownStatus,
} from "../services/pagApi";
import { PagDrilldownModal } from "./PagDrilldownModal";

const TEXT = {
  primary: "text-[13px] font-semibold leading-5 text-[#1e293b] dark:text-white",
  value: "text-[13px] font-normal leading-5 text-[#334155] dark:text-white/80",
  meta: "text-[11px] font-normal leading-4 text-[#94a3b8] dark:text-white/40",
  strong:
    "text-[13px] font-semibold leading-5 tabular-nums text-[#1e293b] dark:text-white",
} as const;

const CELL = "align-middle!";

const W = {
  application: 145,
  criticality: 112,
  department: 145,
  division: 218,
  account: 220,
  reviewCycle: 130,
  dueDate: 140,
  reviewers: 155,
  policyCount: 225,
  pending: 100,
  completed: 110,
  status: 165,
} as const;

const px = (n: number) => `${n}px`;

const HEADER = { headerAlign: "center", cellClassName: CELL } as const;

const TEXT_COL = { ...HEADER, align: "left", cellClassName: `${CELL} break-normal!` } as const;
const FIGURE_COL = { ...HEADER, align: "center" } as const;

const CRITICALITY_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  LOW: {
    label: "Low",
    className:
      "bg-blue-500/20 text-blue-400 dark:bg-blue-500/20 dark:text-blue-400",
  },
  MEDIUM: {
    label: "Medium",
    className:
      "bg-yellow-500/20 text-yellow-400 dark:bg-yellow-500/20 dark:text-yellow-400",
  },
  HIGH: {
    label: "High",
    className:
      "bg-orange-500/20 text-orange-400 dark:bg-orange-500/20 dark:text-orange-400",
  },
  CRITICAL: {
    label: "Critical",
    className:
      "bg-red-500/20 text-red-400 dark:bg-red-500/20 dark:text-red-400",
  },
};

const normalizeCriticality = (value: string) =>
  value.trim().replace(/\s+/g, "_").replace(/-/g, "_").toUpperCase();

function CriticalityBadge({ value }: { value: string }) {
  const meta = CRITICALITY_BADGE[normalizeCriticality(value)];

  if (!meta) {
    const raw = String(value).trim();
    return (
      <span className={TEXT.value}>
        {raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : value}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-2.5 text-[11px] font-bold leading-none ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function CountLink({ value, onClick }: { value: number; onClick: () => void }) {
  if (!value) return <span className={TEXT.strong}>{value}</span>;
  return (
    <Button variant="link"
      onClick={onClick}
      className="justify-start h-auto px-0 py-0 font-normal rounded text-[13px] font-semibold leading-5 tabular-nums text-indigo-600 underline underline-offset-4 hover:text-indigo-500 dark:text-indigo-400"
    >
      {value}
    </Button>
  );
}

/* ── Donut ──────────────────────────────────────────────────────────── */

const DONUT_SIZE = 58;
const DONUT_THICKNESS = 8;

function PolicyCountDonut({ breakdown }: { breakdown: PagPolicyBreakdown }) {
  const radius = (DONUT_SIZE - DONUT_THICKNESS) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = DONUT_SIZE / 2;

  let consumed = 0;
  const arcs = PAG_BREAKDOWN_SEGMENTS.map((seg) => {
    const value = breakdown[seg.key];
    if (!value || !breakdown.total) return null;
    const length = (value / breakdown.total) * circumference;
    const arc = (
      <circle
        key={seg.key}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={seg.color}
        strokeWidth={DONUT_THICKNESS}
        strokeDasharray={`${length} ${circumference - length}`}
        strokeDashoffset={-consumed}
      />
    );
    consumed += length;
    return arc;
  }).filter(Boolean);

  return (
    <svg
      width={DONUT_SIZE}
      height={DONUT_SIZE}
      viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
      className="flex-shrink-0"
      role="img"
      aria-label={`${breakdown.total} policies`}
    >
      {/* Track — also the whole ring when an account has no policies */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={DONUT_THICKNESS}
        className="stroke-muted"
      />
      <g transform={`rotate(-90 ${center} ${center})`}>{arcs}</g>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-[#1e293b] dark:fill-white"
        style={{ fontSize: 15, fontWeight: 600 }}
      >
        {breakdown.total}
      </text>
    </svg>
  );
}

function PolicyCountCell({ breakdown }: { breakdown: PagPolicyBreakdown }) {
  return (
    <div className="flex items-center gap-3">
      <PolicyCountDonut breakdown={breakdown} />
      <div className="flex flex-col gap-[3px]">
        {PAG_BREAKDOWN_SEGMENTS.map((seg) => (
          <span
            key={seg.key}
            className="whitespace-nowrap text-[11px] font-normal leading-4"
            style={{ color: seg.color }}
          >
            {seg.label} - <span className="font-semibold">{breakdown[seg.key]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Report export ──────────────────────────────────────────────────── */

const CSV_HEADERS = [
  "Application Name",
  "Criticality",
  "Department",
  "Division",
  "Account Name",
  "Account ID",
  "Review Cycle",
  "Due Date",
  "Reviewers",
  "Policy Count",
  "Groups",
  "Roles",
  "Service Roles",
  "Users",
  "Pending",
  "Completed",
];

const csvCell = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

function downloadReport(rows: PagReviewRow[], cycle: PagCycle) {
  const body = rows.map((r) =>
    [
      r.applicationName,
      r.criticality,
      r.department,
      r.division,
      r.accountName,
      r.accountId,
      r.reviewCycle,
      safeFormatDate(r.dueDate),
      r.reviewers.join("; "),
      r.policyCount.total,
      r.policyCount.groups,
      r.policyCount.roles,
      r.policyCount.serviceRoles,
      r.policyCount.users,
      r.pending,
      r.completed,
    ]
      .map(csvCell)
      .join(","),
  );

  const csv = [CSV_HEADERS.map(csvCell).join(","), ...body].join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `privilege-access-review-${cycle}-cycle.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function toRows(res: PagCycleListResponse | null | undefined): PagReviewRow[] {
  const dash = (v: string | null | undefined) => (v && v.trim() ? v : "-");
  return (res?.data ?? []).map((r) => {
    const done = completedCount(r);
    return {
      id: `${r.appCode}|${r.accountId}`,
      appCode: r.appCode,
      applicationName: r.appName,
      criticality: dash(r.criticality),
      department: dash(r.businessDepartment),
      division: dash(r.businessDivision),
      accountId: r.accountId,
      accountName: r.accountName,
      reviewCycle: dash(r.reviewCycle),
      /* Epoch seconds here, ISO on the current cycle — safeFormatDate takes
         either, so the column renders the same both ways. */
      dueDate: r.dueDate ? new Date(r.dueDate * 1000).toISOString() : "",
      reviewers: r.reviewers ?? [],
      policyCount: {
        groups: r.groupCount ?? 0,
        roles: r.roleCount ?? 0,
        serviceRoles: r.serviceCount ?? 0,
        users: r.userCount ?? 0,
        total: r.totalStatusCount ?? 0,
      },
      pending: r.pendingStatusCount ?? 0,
      completed: done,
    };
  });
}

export function PagReviewTable({
  appCodes,
  ready = true,
  cycle,
  onCycleChange,
  refreshToken = 0,
}: {
  /** Application filter, sent as the list request's `appCodes`. */
  appCodes: string[];
  refreshToken?: number;
  ready?: boolean;
  cycle: PagCycle;
  onCycleChange: (c: PagCycle) => void;
}) {
  const isPrevious = cycle === "previous";
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0); // DataTable pages are 0-indexed
  const [pageSize, setPageSize] = useState(MAX_PAGE_SIZE);
  const [rows, setRows] = useState<PagReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  /* Opens loading rather than empty: the first request is pending on the
     filter bar, and an empty table in the meantime reads as "no data". */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Which count was clicked, if any. Null keeps the modal closed. */
  const [drilldown, setDrilldown] = useState<{
    row: PagReviewRow;
    status: PagDrilldownStatus;
  } | null>(null);

  const openDrilldown = (row: PagReviewRow, status: PagDrilldownStatus) =>
    setDrilldown({ row, status });

  const handleSearchChange = (next: string) => {
    setSearch(next);
    if (!next.trim()) {
      setAppliedSearch("");
      setPage(0);
    }
  };

  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === appliedSearch) return;
    setAppliedSearch(next);
    setPage(0);
  };

  /* Reset paging when the cycle or the application filter changes, so a
     page-3 position never carries into a smaller result set. */
  useEffect(() => setPage(0), [cycle, appCodes.join(","), refreshToken]);

  /* Bumped when the drill-down closes having changed something, so the
     counts refetch through the effect below. */
  const [selfRefresh, setSelfRefresh] = useState(0);

  /* Both cycles are server-paged and server-searched through the identical
     request body, so only the endpoint differs. */
  const loadList = useCallback(() => {
    if (!ready) return;
    if (appCodes.length === 0) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchList = isPrevious ? fetchPagPreviousCycleList : fetchPagCurrentCycleList;

    setLoading(true);
    setError(null);
    fetchList({ appCodes, keyword: appliedSearch }, page, pageSize)
      .then((res) => {
        if (cancelled) return;
        setRows(toRows(res));
        setTotal(res?.totalElements ?? 0);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(e instanceof Error ? e.message : "Could not load the review list");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, isPrevious, appCodes.join(","), appliedSearch, page, pageSize, refreshToken, selfRefresh]);

  useEffect(() => loadList(), [loadList]);

  const drilldownChanged = useRef(false);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows;

  const columns: Column<PagReviewRow>[] = [
    {
      key: "applicationName",
      header: "Application Name",
      width: px(W.application),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.applicationName}</span>,
    },
    {
      key: "criticality",
      header: "Criticality",
      width: px(W.criticality),
      ...FIGURE_COL,
      render: (r) => <CriticalityBadge value={r.criticality} />,
    },
    {
      key: "department",
      header: "Department",
      width: px(W.department),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.department}</span>,
    },
    {
      key: "division",
      header: "Division",
      width: px(W.division),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.division}</span>,
    },
    {
      key: "accountName",
      header: "Account Name",
      width: px(W.account),
      ...TEXT_COL,
      render: (r) => <span className={TEXT.value}>{r.accountName}</span>,
    },
    {
      key: "reviewCycle",
      header: "Review Cycle",
      width: px(W.reviewCycle),
      ...FIGURE_COL,
      render: (r) => <span className={TEXT.value}>{r.reviewCycle}</span>,
    },
    {
      key: "dueDate",
      /* A closed cycle reports when it finished, not when it was owed. */
      header: isPrevious ? "Completed On" : "Due Date",
      width: px(W.dueDate),
      ...FIGURE_COL,
      render: (r) => (
        <span className={`whitespace-nowrap tabular-nums ${TEXT.value}`}>
          {safeFormatDate(r.dueDate)}
        </span>
      ),
    },
    {
      key: "reviewers",
      header: "Reviewers",
      width: px(W.reviewers),
      ...TEXT_COL,
      render: (r) =>
        r.reviewers.length ? (
          <div className="flex flex-col gap-[3px]">
            {r.reviewers.map((name) => (
              <span key={name} className={`whitespace-nowrap ${TEXT.value}`}>
                {name}
              </span>
            ))}
          </div>
        ) : (
          <span className={TEXT.meta}>-</span>
        ),
    },
    {
      key: "policyCount",
      header: "Policy Count",
      width: px(W.policyCount),
      ...FIGURE_COL,
      render: (r) => <PolicyCountCell breakdown={r.policyCount} />,
    },
    ...(isPrevious
      ? [
        {
          key: "status",
          header: "Status",
          width: px(W.status),
          ...FIGURE_COL,
          render: (r: PagReviewRow) => (
            <Button variant="link"
              onClick={() => openDrilldown(r, "completed")}
              className="justify-start h-auto px-0 py-0 font-normal rounded text-[13px] font-semibold leading-5 text-indigo-600 underline underline-offset-4 hover:text-indigo-500 dark:text-indigo-400"
            >
              <span className="tabular-nums">
                {r.completed}/{r.policyCount.total}
              </span>{" "}
              Completed
            </Button>
          ),
        } satisfies Column<PagReviewRow>,
      ]
      : [
        {
          key: "pending",
          header: "Pending",
          width: px(W.pending),
          ...FIGURE_COL,
          render: (r: PagReviewRow) => (
            <CountLink value={r.pending} onClick={() => openDrilldown(r, "pending")} />
          ),
        } satisfies Column<PagReviewRow>,
        {
          key: "completed",
          header: "Completed",
          width: px(W.completed),
          ...FIGURE_COL,
          render: (r: PagReviewRow) => (
            <CountLink
              value={r.completed}
              onClick={() => openDrilldown(r, "completed")}
            />
          ),
        } satisfies Column<PagReviewRow>,
      ]),
  ];

  const tabClass = (active: boolean) =>
    `border-b-2 pb-1.5 text-sm transition-colors ${active
      ? "border-indigo-500 font-semibold text-foreground"
      : "border-transparent font-medium text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="rounded-xl border border-border bg-card p-5 pt-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Details</h3>

      <div className="mb-4 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-end sm:justify-between">
        {/* Cycle tabs */}
        <div className="flex items-center gap-5">
          <Button variant="ghost" className={`justify-start h-auto px-0 py-0 font-normal ${tabClass(cycle === "current")}`} onClick={() => onCycleChange("current")}>
            Current Cycle
          </Button>
          <Button variant="ghost"
            className={`justify-start h-auto px-0 py-0 font-normal ${tabClass(cycle === "previous")}`}
            onClick={() => onCycleChange("previous")}
          >
            Previous Cycle
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 pb-2">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            onSubmit={submitSearch}
            maxLength={120}
            size="sm"
            className="w-[190px]"
          />

          <Button variant="indigo"
            onClick={() => downloadReport(pageRows, cycle)}
            disabled={!pageRows.length}
            className="justify-start h-[34px] gap-1.5 rounded-lg px-3.5 text-sm font-semibold disabled:cursor-default disabled:opacity-40"
          >
            <FileDown className="size-3.5" />
            Generate Report
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={pageRows}
        loading={loading}
        error={error}
        onRetry={loadList}
        rowKey={(r) => r.id}
        emptyText={
          appCodes.length === 0
            ? "Select an application to view data."
            : "No Data Available"
        }

        page={safePage}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        maxHeight="550px"
        itemLabel="records"
        responsive
      />

      {drilldown && (
        <PagDrilldownModal
          open
          onChanged={() => {
            drilldownChanged.current = true;
          }}
          onOpenChange={(o) => {
            if (o) return;
            setDrilldown(null);
            if (drilldownChanged.current) {
              drilldownChanged.current = false;
              setSelfRefresh((n) => n + 1);
            }
          }}
          accountId={drilldown.row.accountId}
          accountName={drilldown.row.accountName}
          appCode={drilldown.row.appCode}
          status={drilldown.status}
          cycle={cycle}
        />
      )}
    </div>
  );
}
